import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { useAuth0, AuthContext, mockUser } from './contexts/AuthContext';
import Home from './pages/Home';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import TenderDetailAugmented from './pages/TenderDetailAugmented';
import ResponseBuilder from './pages/ResponseBuilder';
import MyTenders from './pages/MyTenders';
import Analytics from './pages/Analytics';
import ResponseDetail from './pages/ResponseDetail';
import Manifest from './pages/Manifest/Manifest';
import Forge from './pages/Forge/Forge';
import Insights from './pages/Insights';
import { InsightsAccessProvider, useInsightsAccess } from './contexts/InsightsAccessContext';
import Login from './pages/Login';
import Callback from './pages/Callback';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import { BookmarkProvider, useBookmarks } from './contexts/BookmarkContext';
import { TenderReviewProvider } from './contexts/TenderReviewContext';
import { AnalysisProvider, useAnalysisGlobal } from './contexts/AnalysisContext';
import { DisqualificationProvider } from './contexts/DisqualificationContext';
import Disqualified from './pages/Disqualified';
import Recommended from './pages/Recommended';
import errorLogger from './utils/errorLogger';
import { initEventLog, logEvent } from './utils/eventLog';
import { markAuthReady, resetAuthReady } from './services/api';
import './App.css';

// Apply the persisted theme before first paint (avoids a light flash on load).
if (typeof document !== 'undefined' && localStorage.getItem('aiquire-theme') === 'dark') {
  document.documentElement.classList.add('dark');
}

// Dev mode configuration
const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

// Simple Protected Route component for Auth0
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <div className="loading-spinner"></div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Insights nav link — only renders when the backend's INSIGHTS_ADMINS
// allowlist confirms the current user. Defined here so it can read the
// context that's provided around <Router>.
const InsightsNavLink = () => {
  const { allowed } = useInsightsAccess();
  if (!allowed) return null;
  return (
    <Link to="/insights">
      Insights <span className="preview-badge">Admin</span>
    </Link>
  );
};

const NavBar = () => {
  const { bookmarkCount } = useBookmarks();
  const { user, logout, isAuthenticated } = useAuth0();
  const { anyRunning, runningAnalyses } = useAnalysisGlobal();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [dark, setDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  const navigate = useNavigate();

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('aiquire-theme', next ? 'dark' : 'light');
  };

  if (!isAuthenticated) {
    return null;  // Don't show navbar for non-authenticated users
  }

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/home" className="nav-brand">
          <img src="/AIquire.png" alt="[AI]Quire" className="nav-logo" />
        </Link>
        <div className="nav-links">
          <Link to="/home">Home</Link>
          {/* DISCOVERY — funnel stage 1: search, saved tenders, disqualified */}
          <div className="nav-dropdown">
            <span className="nav-dropdown-toggle">
              Discovery
              {bookmarkCount > 0 && (
                <span className="bookmark-count-badge">{bookmarkCount}</span>
              )}
              {anyRunning && (
                <span className="analysis-running-badge" title={`${runningAnalyses.length} analysis running`}>
                  <span className="analysis-running-dot"></span>
                </span>
              )}
              <span className="nav-caret">▾</span>
            </span>
            <div className="nav-dropdown-menu">
              <Link to="/dashboard">Search</Link>
              <Link to="/recommended">Recommended</Link>
              <Link to="/my-tenders" className="nav-link-with-badge">
                My Tenders
                {bookmarkCount > 0 && (
                  <span className="bookmark-count-badge">{bookmarkCount}</span>
                )}
              </Link>
              <Link to="/disqualified">Disqualified</Link>
            </div>
          </div>
          <Link to="/forge">FORGE <span className="preview-badge">Preview</span></Link>
          <Link to="/manifest">Manifest <span className="preview-badge">Preview</span></Link>
          <InsightsNavLink />

          {/* User Menu */}
          <button
            className="nav-theme-toggle"
            onClick={toggleTheme}
            title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          >
            {dark ? '☀️' : '🌙'}
          </button>
          <div className="user-menu-container">
            <button
              className="user-menu-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="user-avatar-img" />
              ) : (
                <span className="user-avatar">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
              )}
              <span className="user-name">{user?.name || user?.email || 'User'}</span>
              <span className="dropdown-arrow">▼</span>
            </button>

            {showUserMenu && (
              <div className="user-menu-dropdown">
                <div className="user-menu-header">
                  <div className="user-menu-name">{user?.name}</div>
                  <div className="user-menu-email">{user?.email}</div>
                </div>
                <div className="user-menu-divider"></div>
                <Link to="/settings" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                  <span>⚙️</span> Settings
                </Link>
                <Link to="/admin" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                  <span>👥</span> Admin
                </Link>
                <button className="user-menu-item" onClick={handleLogout}>
                  <span>🚪</span> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Callback />} />

      {/* Protected Routes */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-tenders"
        element={
          <ProtectedRoute>
            <MyTenders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/disqualified"
        element={
          <ProtectedRoute>
            <Disqualified />
          </ProtectedRoute>
        }
      />
      {/* Redirects from old routes */}
      <Route
        path="/recommended"
        element={
          <ProtectedRoute>
            <Recommended />
          </ProtectedRoute>
        }
      />
      <Route path="/bookmarks" element={<Navigate to="/my-tenders" replace />} />
      <Route path="/responses" element={<Navigate to="/my-tenders" replace />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route path="/analytics" element={<Navigate to="/pricing" replace />} />
      <Route
        path="/manifest"
        element={
          <ProtectedRoute>
            <Manifest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manifest/:id"
        element={
          <ProtectedRoute>
            <Manifest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forge"
        element={
          <ProtectedRoute>
            <Forge />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forge/:id"
        element={
          <ProtectedRoute>
            <Forge />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <Insights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/responses/:id"
        element={
          <ProtectedRoute>
            <ResponseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tender/:id"
        element={
          <ProtectedRoute>
            <TenderDetailAugmented />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tender/:id/respond"
        element={
          <ProtectedRoute>
            <ResponseBuilder />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// Inner app content (shared between dev and prod mode)
const AppContent = () => {
  return (
    <TenderReviewProvider>
      <BookmarkProvider>
        <DisqualificationProvider>
        <AnalysisProvider>
          <InsightsAccessProvider>
            <Router>
              <div className="App">
                <NavBar />

                <main className="main-content">
                  <AppRoutes />
                </main>
              </div>
            </Router>
          </InsightsAccessProvider>
        </AnalysisProvider>
        </DisqualificationProvider>
      </BookmarkProvider>
    </TenderReviewProvider>
  );
};

// Syncs Auth0 access token to localStorage for API interceptor and BookmarkContext
const TokenSync = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated) {
      // Hold API requests until the token actually lands. Auth0 flips
      // isAuthenticated before getAccessTokenSilently() resolves, so without
      // this the first calls of a session go out with no Authorization header
      // and come back 401.
      if (!localStorage.getItem('authToken')) resetAuthReady();
      // Fetch and store token, refresh periodically
      const syncToken = async () => {
        try {
          const token = await getAccessTokenSilently();
          const wasAuthed = !!localStorage.getItem('authToken');
          localStorage.setItem('authToken', token);
          if (!wasAuthed) {
            // First time we have a token this session → log auth.login
            logEvent('auth.login');
          }
        } catch (err) {
          console.error('TokenSync: failed to get token:', err);
        } finally {
          // Release waiters either way — a failed token must not hang the app.
          markAuthReady();
        }
      };
      syncToken();
      // Refresh token every 5 minutes (Auth0 tokens typically expire in 24h, but refresh early)
      const interval = setInterval(syncToken, 5 * 60 * 1000);
      return () => clearInterval(interval);
    } else {
      // Clear token on logout
      const hadToken = !!localStorage.getItem('authToken');
      localStorage.removeItem('authToken');
      if (hadToken) {
        logEvent('auth.logout');
      }
      // Logged out: no token is coming, so don't make requests wait for one.
      markAuthReady();
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return null;
};

function App() {
  // Initialize error logger on app startup
  useEffect(() => {
    errorLogger.initialize();
    initEventLog();
  }, []);

  // Log dev mode status
  useEffect(() => {
    if (DEV_MODE) {
      console.log('🔧 DEV MODE: Auth0 authentication bypassed');
      console.log('📧 Logged in as:', mockUser.email);
    }
  }, []);

  // In dev mode, skip Auth0 entirely
  if (DEV_MODE) {
    return (
      <AuthContext.Provider value={{
        isAuthenticated: true,
        isLoading: false,
        user: mockUser,
        loginWithRedirect: () => console.log('Dev mode: login skipped'),
        logout: () => {
          console.log('Dev mode: logout - redirecting to landing');
          window.location.href = '/';
        },
        getAccessTokenSilently: () => Promise.resolve('dev-token'),
        getIdTokenClaims: () => Promise.resolve({ __raw: 'dev-token' })
      }}>
        <AppContent />
      </AuthContext.Provider>
    );
  }

  // Production mode with real Auth0
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin + '/callback',
        audience: process.env.REACT_APP_AUTH0_AUDIENCE
      }}
      cacheLocation="localstorage"
    >
      <TokenSync />
      <AppContent />
    </Auth0Provider>
  );
}

export default App;
